import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  UserCheck, Calendar, DollarSign, Clock, GripVertical,
  Loader2, AlertCircle, MapPin, Users, Award, Star,
  ArrowLeft, Search, X, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Draggable Referee Card
const RefereeCard = ({ referee, isDragging, onDragStart, onDragEnd }) => {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, referee)}
      onDragEnd={onDragEnd}
      className={`p-3 rounded-lg border bg-card cursor-grab active:cursor-grabbing transition-all ${
        isDragging ? 'opacity-50 scale-95' : 'hover:border-primary/50 hover:shadow-md'
      }`}
      data-testid={`referee-card-${referee.id}`}
    >
      <div className="flex items-center gap-3">
        <GripVertical className="w-4 h-4 text-muted-foreground" />
        <div className="flex-1">
          <p className="font-medium">{referee.name}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="text-xs">
              {referee.certification_level || 'Standard'}
            </Badge>
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 text-yellow-500" />
              {referee.rating?.toFixed(1) || '4.5'}
            </span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-green-600">${referee.pay_rate || 25}/game</p>
          <p className="text-xs text-muted-foreground">{referee.games_officiated || 0} games</p>
        </div>
      </div>
    </div>
  );
};

// Droppable Game Card
const GameDropZone = ({ 
  game, 
  assignedReferee, 
  onDrop, 
  onRemoveReferee,
  isDropTarget,
  loading 
}) => {
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDrop={(e) => onDrop(e, game)}
      className={`p-4 rounded-lg border-2 border-dashed transition-all ${
        isDropTarget 
          ? 'border-primary bg-primary/5' 
          : assignedReferee 
            ? 'border-green-500/50 bg-green-500/5' 
            : 'border-muted-foreground/30 hover:border-muted-foreground/50'
      }`}
      data-testid={`game-dropzone-${game.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold">{game.team1_name} vs {game.team2_name}</h4>
          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {game.scheduled_time 
                ? new Date(game.scheduled_time).toLocaleDateString() 
                : 'TBD'}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {game.scheduled_time 
                ? new Date(game.scheduled_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : 'TBD'}
            </span>
            {game.field && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {game.field}
              </span>
            )}
          </div>
        </div>
        <Badge variant={game.status === 'completed' ? 'default' : 'outline'}>
          {game.status}
        </Badge>
      </div>

      {/* Referee Assignment Area */}
      <div className="mt-3 pt-3 border-t">
        {loading ? (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
          </div>
        ) : assignedReferee ? (
          <div className="flex items-center justify-between bg-green-500/10 p-2 rounded">
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-green-600" />
              <span className="font-medium">{assignedReferee.referee_name}</span>
              <Badge variant="outline" className="text-xs">
                {assignedReferee.status}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-500/10"
              onClick={() => onRemoveReferee(game.id, assignedReferee.id)}
              data-testid={`remove-referee-${game.id}`}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="text-center py-3 text-muted-foreground">
            <UserCheck className="w-6 h-6 mx-auto mb-1 opacity-50" />
            <p className="text-sm">Drop referee here to assign</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const RefereeAssignment = () => {
  const { tournamentId } = useParams();
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [referees, setReferees] = useState([]);
  const [games, setGames] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [draggedReferee, setDraggedReferee] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [tournament, setTournament] = useState(null);

  useEffect(() => {
    fetchData();
  }, [tournamentId, token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [refereesRes, gamesRes, tournamentRes] = await Promise.all([
        axios.get(`${API}/referees`, { headers }),
        axios.get(`${API}/games?tournament_id=${tournamentId}&limit=50`, { headers }),
        axios.get(`${API}/tournaments/${tournamentId}`, { headers })
      ]);
      
      setReferees(refereesRes.data);
      setGames(gamesRes.data);
      setTournament(tournamentRes.data);
      
      // Fetch existing assignments
      const assignmentsMap = {};
      for (const game of gamesRes.data) {
        try {
          const assignRes = await axios.get(
            `${API}/games/${game.id}/referees`,
            { headers }
          );
          if (assignRes.data && assignRes.data.length > 0) {
            assignmentsMap[game.id] = assignRes.data[0];
          }
        } catch (e) {
          // No assignment for this game
        }
      }
      setAssignments(assignmentsMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load referee data');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, referee) => {
    setDraggedReferee(referee);
    e.dataTransfer.setData('text/plain', referee.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedReferee(null);
    setDropTarget(null);
  };

  const handleDrop = async (e, game) => {
    e.preventDefault();
    const refereeId = e.dataTransfer.getData('text/plain');
    const referee = referees.find(r => r.id === refereeId);
    
    if (!referee || assignments[game.id]) {
      toast.error('Game already has a referee assigned');
      return;
    }
    
    setAssigning(game.id);
    
    try {
      const response = await axios.post(
        `${API}/games/${game.id}/assign-referee`,
        {
          referee_id: refereeId,
          pay_rate: referee.pay_rate || 25
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAssignments(prev => ({
        ...prev,
        [game.id]: {
          ...response.data,
          referee_name: referee.name
        }
      }));
      
      toast.success(`${referee.name} assigned to ${game.team1_name} vs ${game.team2_name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign referee');
    } finally {
      setAssigning(null);
      setDraggedReferee(null);
    }
  };

  const handleRemoveReferee = async (gameId, assignmentId) => {
    try {
      await axios.delete(
        `${API}/games/${gameId}/unassign-referee/${assignmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAssignments(prev => {
        const newAssignments = { ...prev };
        delete newAssignments[gameId];
        return newAssignments;
      });
      
      toast.success('Referee unassigned');
    } catch (error) {
      toast.error('Failed to remove referee');
    }
  };

  const filteredReferees = referees.filter(r => 
    r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.certification_level?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const unassignedGames = games.filter(g => !assignments[g.id] && g.status !== 'completed');
  const assignedGames = games.filter(g => assignments[g.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to={`/tournaments/${tournamentId}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{t('game.referee')} Assignment</h1>
            <p className="text-muted-foreground">
              {tournament?.name} - Drag referees to assign them to games
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Referees Panel */}
          <div className="lg:col-span-1">
            <Card className="sticky top-20">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserCheck className="w-5 h-5" />
                  Available Referees ({filteredReferees.length})
                </CardTitle>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search referees..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="referee-search"
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto">
                {filteredReferees.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    No referees found
                  </p>
                ) : (
                  filteredReferees.map(referee => (
                    <RefereeCard
                      key={referee.id}
                      referee={referee}
                      isDragging={draggedReferee?.id === referee.id}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Games Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Unassigned Games */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  Unassigned Games ({unassignedGames.length})
                </CardTitle>
                <CardDescription>
                  Drag a referee from the left panel to assign them
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {unassignedGames.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Check className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>All games have referees assigned!</p>
                  </div>
                ) : (
                  unassignedGames.map(game => (
                    <GameDropZone
                      key={game.id}
                      game={game}
                      assignedReferee={assignments[game.id]}
                      onDrop={handleDrop}
                      onRemoveReferee={handleRemoveReferee}
                      isDropTarget={dropTarget === game.id}
                      loading={assigning === game.id}
                    />
                  ))
                )}
              </CardContent>
            </Card>

            {/* Assigned Games */}
            {assignedGames.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    Assigned Games ({assignedGames.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {assignedGames.map(game => (
                    <GameDropZone
                      key={game.id}
                      game={game}
                      assignedReferee={assignments[game.id]}
                      onDrop={handleDrop}
                      onRemoveReferee={handleRemoveReferee}
                      isDropTarget={false}
                      loading={assigning === game.id}
                    />
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefereeAssignment;
